import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Globe, Briefcase, UserPlus, ArrowLeft, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, DemoBadge } from "@/components/common";
import { Avatar } from "@/pages/Members";
import { ReportDialog } from "@/components/ReportDialog";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function MemberProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const authed = user && typeof user === "object";
  const isSelf = authed && user.id === id;

  useEffect(() => {
    setLoading(true);
    api.get(`/members/${id}`).then(({ data }) => setMember(data)).catch(() => setMember(false)).finally(() => setLoading(false));
  }, [id]);

  const connect = async () => {
    if (!authed) { navigate("/login"); return; }
    setSending(true);
    try {
      await api.post("/connections", { to_user_id: id, message: message.trim() || undefined });
      toast.success("Connection request sent!");
      setOpen(false);
      setMessage("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="container-nx section-nx"><Spinner /></div>;
  if (!member) return (
    <div className="container-nx section-nx text-center">
      <p className="text-slate-500">Member not found.</p>
      <Link to="/members" className="btn-outline-navy mt-4 inline-flex">Back to directory</Link>
    </div>
  );

  return (
    <div className="container-nx section-nx max-w-4xl">
      <Link to="/members" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B192C]" data-testid="back-to-members">
        <ArrowLeft className="h-4 w-4" /> Back to directory
      </Link>

      <div className="card-nx overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-[#2563EB] to-[#3B82F6]" />
        <div className="px-6 pb-6 sm:px-8">
          <div className="-mt-10 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <div className="rounded-full ring-4 ring-white">
                <Avatar name={member.name} src={member.photo_url} size="h-20 w-20" />
              </div>
              <div className="pb-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-[#0B192C] font-head">{member.name}</h1>
                  {member.is_demo && <DemoBadge />}
                </div>
                <p className="text-sm text-slate-500">{member.designation || member.profession || "Community member"}</p>
              </div>
            </div>
            {!isSelf && (
              <button onClick={() => (authed ? setOpen(true) : navigate("/login"))} className="btn-gold" data-testid="connect-request-btn">
                <UserPlus className="h-4 w-4" /> Connect
              </button>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {member.industry && <span className="chip-gold">{member.industry}</span>}
            {member.city && <span className="chip"><MapPin className="mr-1 h-3 w-3" />{member.city}{member.state ? `, ${member.state}` : ""}</span>}
            {member.business_name && <span className="chip"><Building2 className="mr-1 h-3 w-3" />{member.business_name}</span>}
          </div>

          {member.intro && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">About</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{member.intro}</p>
            </div>
          )}

          {member.expertise?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Expertise</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {member.expertise.map((e) => <span key={e} className="chip">{e}</span>)}
              </div>
            </div>
          )}

          {member.website && (
            <a href={member.website} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#B8860B] hover:underline" data-testid="member-website">
              <Globe className="h-4 w-4" /> {member.website}
            </a>
          )}

          <div className="mt-6 border-t border-slate-100 pt-4">
            <ReportDialog targetType="member" targetId={member.id} label="Report this member" />
          </div>
        </div>
      </div>

      {member.businesses?.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-[#0B192C] font-head">Businesses</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {member.businesses.map((b) => (
              <Link key={b.id} to={`/businesses/${b.id}`} className="card-nx card-nx-hover flex items-center gap-4 p-5" data-testid={`member-business-${b.id}`}>
                {b.logo_url ? (
                  <img src={b.logo_url} alt={b.name} className="h-14 w-14 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-[#F5F3EF]"><Briefcase className="h-6 w-6 text-[#B8860B]" /></div>
                )}
                <div className="min-w-0">
                  <h3 className="truncate font-semibold text-[#0B192C]">{b.name}</h3>
                  <p className="truncate text-xs text-slate-500">{b.industry} · {b.city}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Connect with {member.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Add a short note to introduce yourself (optional).</p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi, I'd love to connect regarding..." className="min-h-[100px]" data-testid="connect-message-input" />
          <DialogFooter>
            <button onClick={connect} disabled={sending} className="btn-gold" data-testid="connect-send-btn">
              {sending ? "Sending..." : "Send request"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
